/**
 * Category resolver — normalises all three playerCategories shapes into a single
 * resolveCategoryLabel(player, playerCategories, gameTypeKey) interface.
 *
 * Three shapes that exist in the codebase:
 *
 *   Cohort map (client / usePlayerCategorisation hook):
 *     { mixed: { p1: 'A', p2: 'B' }, levels_female: { p3: 'B' }, ... }
 *
 *   Cohort + .categories wrapper (legacy evaluateCategoryMatch):
 *     { mixed: { categories: { p1: 'A' } }, ... }
 *
 *   Per-player (frozen input / Edge Function via buildPlayerCategoriesFromPlayersById):
 *     { p1: { mixed: 'A', levels: 'B' }, p2: { ... } }
 */

const COHORT_KEYS = new Set(['mixed', 'levels', 'levels_male', 'levels_female']);

function detectShape(playerCategories) {
  const keys = Object.keys(playerCategories);
  if (keys.length === 0) return 'per_player';
  return keys.some(k => COHORT_KEYS.has(k)) ? 'cohort' : 'per_player';
}

/**
 * Determine the appropriate game-type key from two teams of player objects.
 * When playerWaitMetrics is provided, mens-doubles-eligible females in an
 * all-effective-male game are classified under 'levels_male' rather than creating
 * a spurious 'mixed' key (they have no physical male on the team but play mens doubles).
 *
 * @param {Array}  team1Players - Player objects with `.gender` and `.id` fields
 * @param {Array}  team2Players
 * @param {Object} [playerWaitMetrics={}]
 * @returns {'mixed'|'levels_male'|'levels_female'}
 */
export function getGameTypeKey(team1Players, team2Players, playerWaitMetrics = {}) {
  const allPlayers = [...team1Players, ...team2Players];
  const hasPhysicalMale   = allPlayers.some(p => (p?.gender || '').toLowerCase() === 'male');
  const hasPhysicalFemale = allPlayers.some(p => (p?.gender || '').toLowerCase() === 'female');

  // If any player is a non-eligible female alongside males (or mixed-only), it's mixed.
  if (hasPhysicalMale && hasPhysicalFemale) {
    // Check if every female is mens-doubles-eligible → effective mens game
    const nonEligibleFemale = allPlayers.some(p =>
      (p?.gender || '').toLowerCase() === 'female' &&
      playerWaitMetrics?.[p?.id]?.mensDoublesEligible !== true
    );
    if (nonEligibleFemale) return 'mixed';
    return 'levels_male';
  }

  if (!hasPhysicalMale && hasPhysicalFemale) {
    // All-female: check if every female is eligible → mens game
    const allEligible = allPlayers.every(
      p => playerWaitMetrics?.[p?.id]?.mensDoublesEligible === true
    );
    return allEligible ? 'levels_male' : 'levels_female';
  }

  return 'levels_male';
}

/**
 * Resolve the category label ('A'|'B'|'C'|'D') for a single player given the
 * playerCategories object (any of the three known shapes) and the game-type key.
 *
 * Returns null when the player is not found, the cohort doesn't exist, or
 * playerCategories is falsy.
 *
 * @param {Object} player            - Player object with an `.id` field
 * @param {Object} playerCategories  - Any of the three known shapes
 * @param {'mixed'|'levels_male'|'levels_female'} gameTypeKey
 * @returns {string|null}
 */
export function resolveCategoryLabel(player, playerCategories, gameTypeKey) {
  if (!player?.id || !playerCategories) return null;

  const shape = detectShape(playerCategories);

  if (shape === 'cohort') {
    const cohortData = playerCategories[gameTypeKey];
    if (!cohortData) return null;
    // Handle the extra .categories wrapper (legacy shape)
    const categoryMap = cohortData.categories || cohortData;
    if (!categoryMap || typeof categoryMap !== 'object') return null;
    return categoryMap[player.id] || null;
  }

  // Per-player shape: { playerId: { mixed: 'A', levels: 'B' } }
  const playerData = playerCategories[player.id];
  if (!playerData || typeof playerData !== 'object') return null;

  if (gameTypeKey === 'mixed') return playerData.mixed || null;
  // Both levels_male and levels_female map to 'levels' in the per-player shape
  return playerData.levels || null;
}
